import {Injectable} from '@angular/core';
import {cloneDeep} from 'lodash-es';
import {FuseNavigationItem} from '@fuse/components/navigation';
import {FuseMockApiService} from '@fuse/lib/mock-api';
import {
    compactNavigation,
    defaultNavigation,
    futuristicNavigation,
    horizontalNavigation,
    hotelNavigation,
    masterNavigation,
    providerNavigation,
    semiMasterNavigation
} from 'app/mock-api/common/navigation/data';
import {AccountRole} from '@enums/AccountRole';

@Injectable({
    providedIn: 'root'
})
export class NavigationMockApi {
    private readonly _compactNavigation: FuseNavigationItem[] = compactNavigation;
    private _defaultNavigation: FuseNavigationItem[] = defaultNavigation;
    private readonly _futuristicNavigation: FuseNavigationItem[] = futuristicNavigation;
    private readonly _horizontalNavigation: FuseNavigationItem[] = horizontalNavigation;

    constructor(private readonly _fuseMockApiService: FuseMockApiService) {
        this.registerHandlers();
    }

    registerHandlers(): void {
        this._fuseMockApiService
            .onGet('api/common/navigation')
            .reply(() => {

                let user: any = null;

                try {
                    user = JSON.parse(localStorage.getItem('user') ?? 'null');

                    switch (user?.rol) {
                        case AccountRole.master:
                            this._defaultNavigation = masterNavigation;
                            break;
                        case AccountRole.semiMaster:
                            this._defaultNavigation = semiMasterNavigation;
                            break;
                        case AccountRole.adminHotel:
                        case AccountRole.accountant:
                            this._defaultNavigation = hotelNavigation;
                            break;
                        case AccountRole.provider:
                            this._defaultNavigation = providerNavigation;
                            break;
                        default:
                            this._defaultNavigation = defaultNavigation;
                            break;
                    }
                } catch (e) {
                    console.error('An error occur, while retrieving navigation by rol', e);
                    this._defaultNavigation = defaultNavigation;
                }

                // ✅ Clonamos antes de modificar para no mutar los exports
                const defaultNav = cloneDeep(this._defaultNavigation);

                // -------------------------------------------------------------------------
                // ✅ Regla SIROC: gateada por la feature flag (user.sirocEnabled), que el
                //    backend resuelve según el rol (Master siempre ON; compañía por su flag;
                //    proveedor si alguna de sus compañías la tiene activa).
                // -------------------------------------------------------------------------
                const sirocEnabled = user?.sirocEnabled === true;

                const isHotelUser =
                    user?.rol === AccountRole.adminHotel || user?.rol === AccountRole.accountant;

                if (isHotelUser) {
                    if (sirocEnabled) {
                        this.ensureSirocItem(defaultNav);
                    } else {
                        this.removeNavItemById(defaultNav, 'hotels.siroc');
                    }
                } else if (user?.rol === AccountRole.provider && !sirocEnabled) {
                    this.removeNavItemById(defaultNav, 'siroc');
                }

                // ─── Regla Pólizas: solo providers con al menos una compañía habilitada ───
                if (user?.rol === AccountRole.provider) {
                    if (localStorage.getItem('polizasMenuEnabled') !== 'true') {
                        this.removeNavItemById(defaultNav, 'providers.polizas');
                    }
                }

                // ─── Regla Pólizas: semiMaster solo ve el menú si tiene ≥1 compañía habilitada ───
                if (user?.rol === AccountRole.semiMaster) {
                    if (localStorage.getItem('polizasSemiMasterEnabled') !== 'true') {
                        this.removeNavItemById(defaultNav, 'semiMaster.polizas');
                    }
                }

                // ─── Regla Pólizas (Compañía): solo si su feature flag isPolizas está activa ───
                if (isHotelUser) {
                    if (localStorage.getItem('companyPolizasMenuEnabled') !== 'true') {
                        this.removeNavItemById(defaultNav, 'hotels.polizas');
                    }
                }

                // ─── Regla Fianzas (Compañía): solo si su feature flag isFianzas está activa ───
                if (isHotelUser) {
                    if (localStorage.getItem('companyFianzasMenuEnabled') !== 'true') {
                        this.removeNavItemById(defaultNav, 'hotels.fianzas');
                    }
                }

                // Fill compact navigation children using the default navigation
                this._compactNavigation.forEach((compactNavItem) => {
                    defaultNav.forEach((defaultNavItem) => {
                        if (defaultNavItem.id === compactNavItem.id) {
                            compactNavItem.children = cloneDeep(defaultNavItem.children);
                        }
                    });
                });

                // Fill futuristic navigation children using the default navigation
                this._futuristicNavigation.forEach((futuristicNavItem) => {
                    defaultNav.forEach((defaultNavItem) => {
                        if (defaultNavItem.id === futuristicNavItem.id) {
                            futuristicNavItem.children = cloneDeep(defaultNavItem.children);
                        }
                    });
                });

                // Fill horizontal navigation children using the default navigation
                this._horizontalNavigation.forEach((horizontalNavItem) => {
                    defaultNav.forEach((defaultNavItem) => {
                        if (defaultNavItem.id === horizontalNavItem.id) {
                            horizontalNavItem.children = cloneDeep(defaultNavItem.children);
                        }
                    });
                });

                return [
                    200,
                    {
                        compact: cloneDeep(this._compactNavigation),
                        default: defaultNav,
                        futuristic: cloneDeep(this._futuristicNavigation),
                        horizontal: cloneDeep(this._horizontalNavigation)
                    }
                ];
            });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private helpers
    // -----------------------------------------------------------------------------------------------------

    private removeNavItemById(nav: FuseNavigationItem[], idToRemove: string): void {
        if (!Array.isArray(nav)) return;

        for (let i = nav.length - 1; i >= 0; i--) {
            const item = nav[i];
            if (item?.id === idToRemove) {
                nav.splice(i, 1);
                continue;
            }
            if (item?.children?.length) {
                this.removeNavItemById(item.children, idToRemove);
            }
        }
    }

    private ensureSirocItem(nav: FuseNavigationItem[]): void {
        const hotelsGroup = nav.find(i => i.id === 'hotels');
        if (!hotelsGroup) return;

        hotelsGroup.children = hotelsGroup.children ?? [];

        const exists = hotelsGroup.children.some(i => i.id === 'hotels.siroc');
        if (exists) return;

        const sirocItem: FuseNavigationItem = {
            id: 'hotels.siroc',
            title: 'SIROC',
            type: 'basic',
            icon: 'heroicons_outline:calendar',
            link: '/hotels/siroc'
        };

        const idx = hotelsGroup.children.findIndex(i => i.id === 'hotels.providers');
        const insertAt = idx >= 0 ? idx + 1 : hotelsGroup.children.length;
        hotelsGroup.children.splice(insertAt, 0, sirocItem);
    }
}
